<script setup lang="ts">
import VCard from '~/components/ui/VCard.vue'

import type Tag from '~/entities/tag'
import type { Locale } from '~/types/locale'
import type TagInformation from '~/types/tagInformations'

type Props = {
  genres: TagInformation[]
  nationalities: Tag[]
  languages: Tag[]
  trackRelease: string
}

defineProps<Props>()

const { locale, t } = useI18n()

function getNationalitiesText(country: string): string {
  switch (locale.value as Locale) {
    case 'fr':
      return `${t('common.artists')} ${t(`tags.nationalities.${country}`).toLocaleLowerCase()}`
    case 'de':
      return `${t('common.artists')} aus ${t(`tags.country.${country}`)}`
    case 'en':
    default:
      return `${t(`tags.nationalities.${country}`)} ${t('common.artists')}`
  }
}

function getLanguagesText(language: string): string {
  const lang = t(`tags.lyrics_lang.${language}`) as string

  if (language === '_none_') return lang

  return locale.value === 'fr'
    ? `${t('common.lyrics')} ${t('common.in')} ${lang.toLocaleLowerCase()}`
    : `${lang} ${t('common.lyrics')}`
}
</script>

<template>
  <VCard class="!tw-bg-green-200">
    <i18n-t
      keypath="influencer.profile.slug.matchingTags.mainTemplate"
      tag="h2"
    >
      <template #blackText>
        <span class="tw-text-h6">
          {{ $t('influencer.profile.slug.matchingTags.blackText') }}
        </span>
      </template>
      <template #greenText>
        <span class="tw-text-h6 tw-text-green-700">
          {{ $t('influencer.profile.slug.matchingTags.greenText') }}
        </span>
      </template>
    </i18n-t>
    <ul>
      <li
        v-if="genres.length > 0"
        class="tw-mt-6 tw-flex tw-items-start tw-gap-2"
      >
        <i class="fas fa-square-check tw-text-xl tw-text-green-700"></i>
        <div class="tw-flex tw-flex-wrap">
          <ul class="tw-flex tw-flex-wrap">
            <li
              v-for="(el, index) in genres"
              :key="index"
              class="tagListItemMiddot tw-mb-2 tw-flex-shrink-0"
            >
              <span class="tw-text-body">{{
                $t(`tags.subgenre.${el.tag.name}`)
              }}</span>
            </li>
          </ul>
        </div>
      </li>
      <li
        v-if="nationalities.length > 0"
        class="tw-mt-6 tw-flex tw-items-start tw-gap-2"
      >
        <i class="fas fa-square-check tw-text-xl tw-text-green-700"></i>
        <ul class="tw-flex">
          <li
            v-for="(el, index) in nationalities"
            :key="index"
            class="tagListItemMiddot first-letter:tw-capitalize"
          >
            <span class="tw-text-body">{{
              getNationalitiesText(el.name)
            }}</span>
          </li>
        </ul>
      </li>
      <li
        v-if="languages.length > 0"
        class="tw-mt-6 tw-flex tw-items-start tw-gap-2"
      >
        <i class="fas fa-square-check tw-text-xl tw-text-green-700" />
        <ul>
          <li
            v-for="(el, index) in languages"
            :key="index"
            class="tagListItemMiddot"
          >
            <span class="tw-text-body">{{ getLanguagesText(el.name) }}</span>
          </li>
        </ul>
      </li>
      <li v-if="trackRelease" class="tw-mt-6 tw-flex tw-items-start tw-gap-2">
        <i class="fas fa-square-check tw-text-xl tw-text-green-700" />
        <span class="tw-text-body">{{ trackRelease }}</span>
      </li>
    </ul>
  </VCard>
</template>

<style lang="scss" scoped>
.tagListItemMiddot {
  &:not(:last-of-type)::after {
    content: '·';
    @apply tw-mx-1;
  }
}
</style>
